import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { CatHeading } from 'components/blocks-components';
import {
  MyEquipmentButton,
  MyEquipmentDrawer,
  MyEquipmentDrawerLinkImage,
  MyEquipmentHeader,
  MyEquipmentLinkContent,
  useBreakpoint,
  useDrawer
} from 'components/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { SERIAL_NUMBER_HELP_ASSETS } from '../../../constants';
import styles from '../style.module.scss';

export interface SerialNumberHelpDrawerProps {
  showBackButton: boolean;
  isChild: boolean;
}
export const SerialNumberHelpDrawer: FC<SerialNumberHelpDrawerProps> = ({
  showBackButton,
  isChild
}) => {
  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const drawerSerialLink = useDrawer({
    id: 'blocks-my-equipment-drawer-serial-link'
  });

  const onCloseFindSerial = (e) => {
    e.stopPropagation();
    drawerSerialLink.drawerProps.onBlDrawerClose(e);
  };

  const onBackButtonClickOnSerial = () => {
    drawerSerialLink.closeDrawer();
  };

  const onDrawerSerialLinkClick = (e) => {
    e.stopPropagation();
    drawerSerialLink.initiatorProps.onClick();
  };

  const {
    ASSET_PLATE_BEFORE,
    ASSET_PLATE_BEFORE_MODEL,
    ENGINES_AND_GENERATORS,
    ENGINES_AND_GENERATORS_MODEL,
    NEWER_ASSETS,
    NEWER_ASSETS_MODEL,
    WHERE_TO_FIND_SERIAL
  } = SERIAL_NUMBER_HELP_ASSETS;

  return (
    <div>
      <MyEquipmentButton
        onClick={(e) => onDrawerSerialLinkClick(e)}
        aria-label={t('FIND_SERIAL_GUIDE_TEXT')}
        variant={'ghost'}
        className={`${styles['find-equipment-guide-text']} p-0 mt-2 mb-3`}
      >
        <span className={`${styles['button-link--padding']}`}>
          {t('FIND_SERIAL_GUIDE_TEXT')}
        </span>
      </MyEquipmentButton>
      <MyEquipmentDrawer
        drawer={drawerSerialLink}
        size="lg"
        onCloseHandler={onCloseFindSerial}
        isChild={isChild}
      >
        <MyEquipmentHeader
          className="d-flex"
          variant={'title-sm'}
          onBackButtonClick={showBackButton && onBackButtonClickOnSerial}
        >
          {t('FIND_SERIAL_GUIDE_TEXT')}
        </MyEquipmentHeader>

        <MyEquipmentLinkContent classname={styles['grid']}>
          <MyEquipmentHeader
            className="pb-3 pb-md-0"
            variant={isMD ? 'title' : 'headline'}
          >
            {t('FMS_SLIDE1_TITLE')}
          </MyEquipmentHeader>
          <MyEquipmentDrawerLinkImage
            src={NEWER_ASSETS.src}
            alt={NEWER_ASSETS.alt}
            classname={styles['image']}
          />
          <MyEquipmentHeader className="py-3" variant="body-sm">
            {t('FMS_SLIDE1_BODY')}
          </MyEquipmentHeader>
          <MyEquipmentDrawerLinkImage
            src={NEWER_ASSETS_MODEL.src}
            alt={NEWER_ASSETS_MODEL.alt}
          />
        </MyEquipmentLinkContent>

        <MyEquipmentLinkContent>
          <MyEquipmentHeader className="pt-3 pb-md-3 pb-1" variant="label">
            {t('FMS_SLIDE1_BODY2')}
          </MyEquipmentHeader>
        </MyEquipmentLinkContent>
        <hr />
        <MyEquipmentLinkContent classname={styles['grid']}>
          <MyEquipmentHeader
            className="pb-3 pt-1 p-md-0 "
            variant={isMD ? 'title' : 'headline'}
          >
            {t('FMS_SLIDE2_TITLE')}
          </MyEquipmentHeader>
          <MyEquipmentDrawerLinkImage
            src={ASSET_PLATE_BEFORE.src}
            classname={styles['image']}
            alt={ASSET_PLATE_BEFORE.alt}
          />
          <MyEquipmentDrawerLinkImage
            classname="d-block pt-3 pt-md-0"
            src={ASSET_PLATE_BEFORE_MODEL.src}
            alt={ASSET_PLATE_BEFORE_MODEL.alt}
          />
        </MyEquipmentLinkContent>
        <MyEquipmentLinkContent>
          <MyEquipmentHeader className="pb-3 d-inline-block" variant="body-sm">
            {t('FMS_SLIDE2_BODY')}
          </MyEquipmentHeader>
          <MyEquipmentHeader className="pb-3" variant="body-sm">
            {t('FMS_SLIDE2_BODY2')}
          </MyEquipmentHeader>
          <MyEquipmentHeader className="pb-3" variant="body-sm">
            <CatHeading variant="label" className="d-inline-block">
              {t('PREFIX')}
            </CatHeading>
            {t('FMS_SLIDE2_BODY_PREFIX')}
          </MyEquipmentHeader>
          <MyEquipmentHeader className="pb-0 pb-md-3" variant="body-sm">
            <CatHeading variant="label" className="d-inline-block">
              {t('SEQUENCE_ID')}
            </CatHeading>
            {t('FMS_SLIDE2_BODY_SEQID')}
          </MyEquipmentHeader>
        </MyEquipmentLinkContent>

        <hr />
        <MyEquipmentLinkContent classname={styles['grid']}>
          <MyEquipmentHeader
            className="pb-3 pt-1 -md-0"
            variant={isMD ? 'title' : 'headline'}
          >
            {t('FMS_SLIDE3_TITLE')}
          </MyEquipmentHeader>
          <MyEquipmentDrawerLinkImage
            src={ENGINES_AND_GENERATORS.src}
            classname={styles['image']}
            alt={ENGINES_AND_GENERATORS.alt}
          />
          <MyEquipmentHeader className="py-3" variant="body-sm">
            {t('FMS_SLIDE3_BODY')}
          </MyEquipmentHeader>
          <MyEquipmentDrawerLinkImage
            src={ENGINES_AND_GENERATORS_MODEL.src}
            alt={ENGINES_AND_GENERATORS_MODEL.alt}
          />
        </MyEquipmentLinkContent>
        <hr />
        <MyEquipmentLinkContent classname={styles['grid']}>
          <MyEquipmentHeader
            className="pb-3 pt-1 p-md-0"
            variant={isMD ? 'title' : 'headline'}
          >
            {t('FMS_SLIDE4_TITLE')}
          </MyEquipmentHeader>
          <MyEquipmentDrawerLinkImage
            classname={styles['image']}
            src={WHERE_TO_FIND_SERIAL.src}
            alt={WHERE_TO_FIND_SERIAL.alt}
          />
          <MyEquipmentHeader className="py-3" variant="body-sm">
            {t('FMS_SLIDE4_BODY')}
          </MyEquipmentHeader>
        </MyEquipmentLinkContent>
      </MyEquipmentDrawer>
    </div>
  );
};
